import sanitizeSearchQueryForLog from '@/components/TypeAhead/sanitize-search-query-for-log'
import type { GA4PageNameType } from '@/ga4types'
import analytics from '@/shared-utils/analytics'

import { AI_SEARCH_MODE } from './aiSearchConstants'

function isControlCharacter(char: string): boolean {
  const code = char.codePointAt(0)
  return code !== undefined && (code <= 0x1f || code === 0x7f)
}

/** Control-character sanitize without the 256-char cap used on typeahead logs. */
function sanitizeFullSearchQuery(searchQuery: string) {
  return searchQuery
    .trim()
    .split('')
    .map((char) => (isControlCharacter(char) ? ' ' : char))
    .join('')
    .replaceAll(/\s+/g, ' ')
}

// cguid, page URL and device are attached to every browser log by the beacon —
// these only carry what is specific to the event.

function fireNlSearchToggle(pageName: GA4PageNameType) {
  analytics.fireGA4Event({
    event: 'internal_link',
    attributes: {
      type: 'nl_search',
      link_name: 'nl_search',
      page_name: pageName,
      product_name: 'hotel',
    },
  })
}

function fireNlSearchSubmit(pageName: GA4PageNameType, itemName: 'nl_search' | 'nl_search_penny') {
  analytics.fireGA4Event({
    event: 'search',
    attributes: {
      type: 'nl_search',
      item_name: itemName,
      page_name: pageName,
      product_name: 'hotel',
    },
  })
}

export type LogAiSearchModeToggleParams = {
  toMode: string
  pageName: GA4PageNameType
}

/** Splunk: message=ai_search_mode_toggle. GA4 only when the user switches into AI search. */
export function logAiSearchModeToggle({ toMode, pageName }: LogAiSearchModeToggleParams) {
  analytics.log({
    message: 'ai_search_mode_toggle',
    toMode,
  })

  if (toMode === AI_SEARCH_MODE) {
    fireNlSearchToggle(pageName)
  }
}

/** Splunk: message=ai_search_no_recommendations_switch_to_standard */
export function logAiSearchNoRecommendationsSwitch(searchQuery: string) {
  analytics.log({
    message: 'ai_search_no_recommendations_switch_to_standard',
    searchQuery: sanitizeSearchQueryForLog(searchQuery),
  })
}

/** Splunk: message=ai_search_resolve_failed */
export function logAiSearchResolveFailed(searchQuery: string) {
  analytics.log({
    message: 'ai_search_resolve_failed',
    searchQuery: sanitizeSearchQueryForLog(searchQuery),
  })
}

/** Splunk: message=ai_search_request_failed */
export function logAiSearchRequestFailed(searchQuery: string) {
  analytics.log({
    message: 'ai_search_request_failed',
    searchQuery: sanitizeSearchQueryForLog(searchQuery),
  })
}

/** Splunk: message=ai_search_request_timeout */
export function logAiSearchRequestTimeout(searchQuery: string) {
  analytics.log({
    message: 'ai_search_request_timeout',
    searchQuery: sanitizeSearchQueryForLog(searchQuery),
  })
}

export type LogAiSearchQueryEventParams = {
  searchQuery: string
  pageName: GA4PageNameType
}

/** Splunk: message=ai_search_penny_redirect — full entered query, no length cap. */
export function logAiSearchPennyRedirect({ searchQuery, pageName }: LogAiSearchQueryEventParams) {
  analytics.log({
    message: 'ai_search_penny_redirect',
    searchQuery: sanitizeFullSearchQuery(searchQuery),
  })
  fireNlSearchSubmit(pageName, 'nl_search_penny')
}

/** Splunk: message=ai_search_relax_redirect — full entered query, no length cap. */
export function logAiSearchRelaxRedirect({ searchQuery, pageName }: LogAiSearchQueryEventParams) {
  analytics.log({
    message: 'ai_search_relax_redirect',
    searchQuery: sanitizeFullSearchQuery(searchQuery),
  })
  fireNlSearchSubmit(pageName, 'nl_search')
}
